import { Formik } from "formik";
import React, { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useGetCameraConfigQuery,
  useUpdateConfigurationSingleDeviceMutation,
} from "../../../../api/network/vmApiService";
import { setSingleVMCameraCurrentConfigs } from "../../../../api/store/commonSlice";
import { CameraDeviceConfigProfile } from "../../../../api/types/Camera";
import { AIEventConfiguration } from "../../../../api/types/CamerasAdminVM";
import svgIcons from "../../../../assets/svg/svgIcons";
import { AdminFormFieldRadioCheckbox } from "../../../../components/admin/formFields";
import { APP_CONFIG } from "../../../../constants/constants";
import { routeUrls } from "../../../../navigation/routeUrls";
import HeaderView from "../../components/HeaderView";
import SubHeaderView from "../../components/SubHeaderView";
import {
  aiEventDetectionFormInitialValues,
  aiEventDetectionFormValidationSchema,
} from "../../validation";
import EventField from "./event-field";
import { useFormikContext } from "formik";
import { MASS_CONFIG_DEFAULTS } from "./mass-config-defaults";
type MassGroup = "adas" | "dms";
const MASS_FIELDS = ["sensitive", "videoBefore", "videoAfter", "eventDebounce"] as const;

const MassConfigSync = ({
  group,
  config,
}: {
  group: MassGroup;
  config?: Record<string, any> | null;
}) => {
  const { values, setFieldValue } = useFormikContext<Record<string, any>>();
  useEffect(() => {
    MASS_FIELDS.forEach((field) => {
      const name = `mass_${group}_${field}`;
      const raw = config?.[field];
      const next = raw === "" ? undefined : raw; // empty = "not set"
      if (!Object.is(values[name], next)) {
        setFieldValue(name, next, true);
      }
    });
  });
  return null;
};

const UNSUPPORTED_FIELDS: Record<string, string[]> = {
  lensCovered: ["sensitive"],
};

const ScreenAIEventDetction: FC = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "camera.dashboard.management.ai_event_detection",
  });
  const navigationTypeRef = useRef<"none" | "next" | "previous">("none");
  const { cameraId } = useParams<{ cameraId: string }>();
  const { state: deviceData } = useLocation();
  const model = deviceData?.model || "";
  const isSingleConfiguration = deviceData?.isSingleConfiguration || false;
  const isMassSingleConfiguration = deviceData?.isMassSingleConfiguration || false;
  const cameraIds = deviceData?.cameraIds || [];
  const cameraName = deviceData?.cameraName || null;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const isAdas = pathname.includes("adas");
  const allCameraConfigsInStore = useSelector(
    (state: {
      commonReducer: {
        singleVMCameraCurrentConfigs: {
          [key: string]: CameraDeviceConfigProfile;
        };
      };
    }) => state.commonReducer.singleVMCameraCurrentConfigs
  );
  const thisCameraConfigInStore =
    allCameraConfigsInStore?.[cameraId as string] || {};
  const [selectedEvent, setSelectedEvent] =
    useState<string>(isAdas ? "adasEnable" : "dmsEnable");
  const [updateConfigurationSingleApiDeviceTrigger] =
    useUpdateConfigurationSingleDeviceMutation();
  const [allAdas, setAllAdas] = useState<boolean>(false);
  const [allDms, setAllDms] = useState<boolean>(false);

  const buildEventPayload = (values: any, events: string[]) => {
    const payload: Record<string, any> = {};
    events.forEach((eventField) => {
      const isEnabled = values[eventField];
      const eventKey = eventField
        .replace(/^adas_/, "")
        .replace(/^dms_/, "")
        .replace(/Enable$/, "");

      if (isEnabled) {
        const skip = UNSUPPORTED_FIELDS[eventKey] ?? [];
        const setField = (field: string, value: any) => {
          if (!skip.includes(field)) payload[`${eventKey}_${field}`] = value;
        };

        payload[eventField] = true;
        setField("sensitive", values[`${eventKey}_sensitive`]);
        setField("videoBefore", values[`${eventKey}_videoBefore`]);
        setField("videoAfter", values[`${eventKey}_videoAfter`]);
        setField("audioAlert", values[`${eventKey}_audioAlert`]);
        setField("media", values[`${eventKey}_media`]);
        setField("eventDebounce", values[`${eventKey}_eventDebounce`]);
      } else {
        payload[eventField] = false;
      }
    });
    return payload;
  };

  const ADAS_EVENTS = [
    "adas_frontalEnable",
    "adas_pedestrianCollisionEnable",
    "adas_tailgatingEnable",
    "adas_laneEnable",
    "adas_stopandgoEnable",
  ];
  const DMS_EVENT = [
    "dms_fatigueEnable",
    "dms_seatbeltUnfastenEnable",
    "dms_distractionEnable",
    "dms_phoneEnable",
    "dms_lensCoveredEnable",
    "dms_smokingEnable"
  ];

  const [massConfigValues, setMassConfigValues] = useState<{
    adas: typeof MASS_CONFIG_DEFAULTS | null;
    dms: typeof MASS_CONFIG_DEFAULTS | null;
  }>({
    adas: null,
    dms: null,
  });

  const handleLeftNavigation = () => {
    if (isAdas) {
      navigate(
        isSingleConfiguration || isMassSingleConfiguration
          ? `${routeUrls.dashboardChildren.cameraChildren.sensor_event_detection}/${cameraId}`
          : routeUrls.dashboardChildren.cameraChildren.sensor_event_detection,
        {
          state: {
            isSingleConfiguration: isSingleConfiguration,
            isMassSingleConfiguration: isMassSingleConfiguration,
            cameraIds: cameraIds,
            model: model,
            cameraName: cameraName
          },
        }
      );
    }
    else {
      navigate(
        isSingleConfiguration || isMassSingleConfiguration
          ? `${routeUrls.dashboardChildren.cameraChildren.ai_event_detection_adas}/${cameraId}`
          : routeUrls.dashboardChildren.cameraChildren.ai_event_detection_adas,
        {
          state: {
            isSingleConfiguration: isSingleConfiguration,
            isMassSingleConfiguration: isMassSingleConfiguration,
            cameraIds: cameraIds,
            model: model,
            cameraName: cameraName
          },
        }
      );
      setSelectedEvent("adasEnable");
    }
  };

  const handleRightNavigation = () => {
    if (isAdas) {
      navigate(
        isSingleConfiguration || isMassSingleConfiguration
          ? `${routeUrls.dashboardChildren.cameraChildren.ai_event_detection_dms}/${cameraId}`
          : routeUrls.dashboardChildren.cameraChildren.ai_event_detection_dms,
        {
          state: {
            isSingleConfiguration: isSingleConfiguration,
            isMassSingleConfiguration: isMassSingleConfiguration,
            cameraIds: cameraIds,
            model: model,
            cameraName: cameraName
          },
        }
      );
      setSelectedEvent("dmsEnable");
    }
    else {
      navigate(
        isSingleConfiguration || isMassSingleConfiguration
          ? `${routeUrls.dashboardChildren.cameraChildren.traffic_signal_detection}/${cameraId}`
          : routeUrls.dashboardChildren.cameraChildren.traffic_signal_detection,
        {
          state: {
            isSingleConfiguration: isSingleConfiguration,
            isMassSingleConfiguration: isMassSingleConfiguration,
            cameraIds: cameraIds,
            model: model,
            cameraName: cameraName
          },
        }
      );
    }
  };

  const {
    data: dataSingleCameraConfig,
    isFetching: isFetchingSingleCameraConfig,
  } = useGetCameraConfigQuery(cameraId || "", {
    skip: (!isSingleConfiguration && !isMassSingleConfiguration) || !cameraId,
    refetchOnMountOrArgChange: true,
  });

  const memoizedInitialValues = React.useMemo(() => {
    const initialValues = aiEventDetectionFormInitialValues(
      !isSingleConfiguration && !isMassSingleConfiguration
    );
    if (!dataSingleCameraConfig?.data?.current || !cameraId)
      return initialValues;
    const {
      data: { current },
    } = dataSingleCameraConfig;
    // here we seed store with freshly fetched configuration
    setTimeout(() => {
      dispatch(
        setSingleVMCameraCurrentConfigs({
          cid: cameraId,
          profile: current,
        })
      );
    }, 100);
    return {
      ...initialValues,

      // adas side
      adasEnable: current.aiEventDetection.adasEnable,
      adas_frontalEnable: current.aiEventDetection.adas_frontalEnable,
      adas_pedestrianCollisionEnable:
        current.aiEventDetection.adas_pedestrianCollisionEnable,
      adas_tailgatingEnable: current.aiEventDetection.adas_tailgatingEnable,
      adas_laneEnable: current.aiEventDetection.adas_laneEnable,
      adas_stopandgoEnable: current.aiEventDetection.adas_stopandgoEnable,

      //dms side
      dmsEnable: current.aiEventDetection.dmsEnable,
      dms_fatigueEnable: current.aiEventDetection.dms_fatigueEnable,
      dms_seatbeltUnfastenEnable:
        current.aiEventDetection.dms_seatbeltUnfastenEnable,
      dms_distractionEnable: current.aiEventDetection.dms_distractionEnable,
      dms_phoneEnable: current.aiEventDetection.dms_phoneEnable,
      dms_lensCoveredEnable: current.aiEventDetection.dms_lensCoveredEnable,
      dms_smokingEnable: current.aiEventDetection.dms_smokingEnable,

      //adas -frontal
      frontal_sensitive: current.aiEventDetection.frontal_sensitive,
      frontal_videoBefore: current.aiEventDetection.frontal_videoBefore,
      frontal_videoAfter: current.aiEventDetection.frontal_videoAfter,
      frontal_audioAlert: current.aiEventDetection.frontal_audioAlert,
      frontal_eventDebounce: current.aiEventDetection.frontal_eventDebounce,
      frontal_media: current.aiEventDetection.frontal_media,

      //adas - pedestrian
      pedestrianCollision_sensitive:
        current.aiEventDetection.pedestrianCollision_sensitive,
      pedestrianCollision_videoBefore:
        current.aiEventDetection.pedestrianCollision_videoBefore,
      pedestrianCollision_videoAfter:
        current.aiEventDetection.pedestrianCollision_videoAfter,
      pedestrianCollision_audioAlert:
        current.aiEventDetection.pedestrianCollision_audioAlert,
      pedestrianCollision_eventDebounce:
        current.aiEventDetection.pedestrianCollision_eventDebounce,
      pedestrianCollision_media:
        current.aiEventDetection.pedestrianCollision_media,

      //adas - tailgating
      tailgating_sensitive: current.aiEventDetection.tailgating_sensitive,
      tailgating_videoBefore: current.aiEventDetection.tailgating_videoBefore,
      tailgating_videoAfter: current.aiEventDetection.tailgating_videoAfter,
      tailgating_audioAlert: current.aiEventDetection.tailgating_audioAlert,
      tailgating_eventDebounce:
        current.aiEventDetection.tailgating_eventDebounce,
      tailgating_media: current.aiEventDetection.tailgating_media,

      //adas - lane
      lane_sensitive: current.aiEventDetection.lane_sensitive,
      lane_videoBefore: current.aiEventDetection.lane_videoBefore,
      lane_videoAfter: current.aiEventDetection.lane_videoAfter,
      lane_audioAlert: current.aiEventDetection.lane_audioAlert,
      lane_eventDebounce: current.aiEventDetection.lane_eventDebounce,
      lane_media: current.aiEventDetection.lane_media,

      //adas - stopandgo
      stopandgo_sensitive: current.aiEventDetection.stopandgo_sensitive,
      stopandgo_videoBefore: current.aiEventDetection.stopandgo_videoBefore,
      stopandgo_videoAfter: current.aiEventDetection.stopandgo_videoAfter,
      stopandgo_audioAlert: current.aiEventDetection.stopandgo_audioAlert,
      stopandgo_eventDebounce: current.aiEventDetection.stopandgo_eventDebounce,
      stopandgo_media: current.aiEventDetection.stopandgo_media,

      //dms - fatigue
      fatigue_sensitive: current.aiEventDetection.fatigue_sensitive,
      fatigue_videoBefore: current.aiEventDetection.fatigue_videoBefore,
      fatigue_videoAfter: current.aiEventDetection.fatigue_videoAfter,
      fatigue_audioAlert: current.aiEventDetection.fatigue_audioAlert,
      fatigue_eventDebounce: current.aiEventDetection.fatigue_eventDebounce,
      fatigue_media: current.aiEventDetection.fatigue_media,

      //dms - distracted
      distraction_sensitive: current.aiEventDetection.distraction_sensitive,
      distraction_videoBefore: current.aiEventDetection.distraction_videoBefore,
      distraction_videoAfter: current.aiEventDetection.distraction_videoAfter,
      distraction_audioAlert: current.aiEventDetection.distraction_audioAlert,
      distraction_eventDebounce:
        current.aiEventDetection.distraction_eventDebounce,
      distraction_media: current.aiEventDetection.distraction_media,

      //dms - phone
      phone_sensitive: current.aiEventDetection.phone_sensitive,
      phone_videoBefore: current.aiEventDetection.phone_videoBefore,
      phone_videoAfter: current.aiEventDetection.phone_videoAfter,
      phone_audioAlert: current.aiEventDetection.phone_audioAlert,
      phone_eventDebounce: current.aiEventDetection.phone_eventDebounce,
      phone_media: current.aiEventDetection.phone_media,

      //dms - seatbelt
      seatbeltUnfasten_sensitive:
        current.aiEventDetection.seatbeltUnfasten_sensitive,
      seatbeltUnfasten_videoBefore:
        current.aiEventDetection.seatbeltUnfasten_videoBefore,
      seatbeltUnfasten_videoAfter:
        current.aiEventDetection.seatbeltUnfasten_videoAfter,
      seatbeltUnfasten_audioAlert:
        current.aiEventDetection.seatbeltUnfasten_audioAlert,
      seatbeltUnfasten_eventDebounce:
        current.aiEventDetection.seatbeltUnfasten_eventDebounce,
      seatbeltUnfasten_media: current.aiEventDetection.seatbeltUnfasten_media,

      //dms - lens obstruction
      lensCovered_sensitive: current.aiEventDetection.lensCovered_sensitive,
      lensCovered_videoBefore: current.aiEventDetection.lensCovered_videoBefore,
      lensCovered_videoAfter: current.aiEventDetection.lensCovered_videoAfter,
      lensCovered_eventDebounce: current.aiEventDetection.lensCovered_eventDebounce,
      lensCovered_audioAlert: current.aiEventDetection.lensCovered_audioAlert,
      lensCovered_media: current.aiEventDetection.lensCovered_media,

      //dms - smoking detection
      smoking_sensitive: current.aiEventDetection.smoking_sensitive,
      smoking_videoBefore: current.aiEventDetection.smoking_videoBefore,
      smoking_videoAfter: current.aiEventDetection.smoking_videoAfter,
      smoking_eventDebounce: current.aiEventDetection.smoking_eventDebounce,
      smoking_audioAlert: current.aiEventDetection.smoking_audioAlert,
      smoking_media: current.aiEventDetection.smoking_media,
    };
  }, [isSingleConfiguration, isMassSingleConfiguration, dataSingleCameraConfig]);

  if (isAdas) {
    return (
      <Formik
        enableReinitialize={true}
        initialValues={memoizedInitialValues}
        validationSchema={aiEventDetectionFormValidationSchema}
        onSubmit={(values, { setSubmitting }) => {
          const navigationType = navigationTypeRef.current;
          const updatedValues: Record<string, any> = { ...values };
          const adasMassConfig = allAdas
            ? massConfigValues.adas ?? MASS_CONFIG_DEFAULTS
            : null;
          if (adasMassConfig) {
            ADAS_EVENTS.forEach((event) => {
              const eventKey = event
                .replace(/^adas_/, "")
                .replace(/Enable$/, "");
              // Enable the individual event
              updatedValues[event] = true;

              updatedValues[`${eventKey}_sensitive`] =
                Number(adasMassConfig.sensitive);

              updatedValues[`${eventKey}_videoBefore`] =
                Number(adasMassConfig.videoBefore);

              updatedValues[`${eventKey}_videoAfter`] =
                Number(adasMassConfig.videoAfter);

              updatedValues[`${eventKey}_eventDebounce`] =
                Number(adasMassConfig.eventDebounce);

              updatedValues[`${eventKey}_media`] =
                adasMassConfig.media;

              updatedValues[`${eventKey}_audioAlert`] =
                adasMassConfig.audioAlert;
            });
          }
          if (allAdas || ADAS_EVENTS.some((event) => Boolean(updatedValues[event]))) {
            updatedValues.adasEnable = true;
          }
          const adasPayload: AIEventConfiguration = buildEventPayload(
            updatedValues,
            ADAS_EVENTS
          );
          const payload: AIEventConfiguration = {
            adasEnable: updatedValues.adasEnable,
            ...adasPayload,
          };

          const updateAllCameras = async () => {
            try {
              for (const id of cameraIds) {
                await updateConfigurationSingleApiDeviceTrigger({
                  cameraId: id,
                  aiEventDetection: payload,
                }).unwrap();
              }
              toast.success("All cameras updated successfully");
              if (navigationType === "next") {
                handleRightNavigation();
              } else if (navigationType === "previous") {
                handleLeftNavigation();
              }
            } catch (err: any) {
              console.error(err);
              toast.error("Failed to update one or more cameras");
            } finally {
              setSubmitting(false);
            }
          };
          if (isSingleConfiguration || isMassSingleConfiguration) {
            updateConfigurationSingleApiDeviceTrigger({
              cameraId: cameraId,
              aiEventDetection: payload,
            })
              .unwrap()
              .then((data) => {
                if (data.message && data.result) {
                  // update store with updated camera config
                  dispatch(
                    setSingleVMCameraCurrentConfigs({
                      cid: cameraId as string,
                      profile: {
                        ...thisCameraConfigInStore,
                        aiEventDetection: {
                          ...thisCameraConfigInStore.aiEventDetection,
                          ...payload,
                        },
                      },
                    })
                  );
                  toast.success("Updated Successfully");
                  if (navigationType === "next") {
                    handleRightNavigation();
                  } else if (navigationType === "previous") {
                    handleLeftNavigation();
                  }
                } else {
                  toast.error("Updation Failed");
                }
              })
              .catch((errors) => {
                console.error(errors);
                toast.error(errors?.join(""));
              })
              .finally(() => {
                setSubmitting(false);
              });
          } else {
            updateAllCameras();
          }
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          setFieldTouched,
          isSubmitting,
          dirty,
          isValid,
          resetForm,
        }) => {

          return (
            <div className="p-4 bg-gray-100 relative">
              <MassConfigSync group="adas" config={allAdas ? massConfigValues.adas ?? MASS_CONFIG_DEFAULTS : null} />
              {isFetchingSingleCameraConfig && (
                <div className="absolute w-full h-full top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white/70 z-10 flex items-center justify-center">
                  <img
                    src={svgIcons.LoadingAnimation}
                    alt=""
                    className="size-12"
                  />
                </div>
              )}
              {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
                <div className="flex text-[10px]">
                  <pre className="flex w-1/3 whitespace-break-spaces break-all">
                    {JSON.stringify(memoizedInitialValues, null, 2)}
                  </pre>
                  <pre className="flex w-1/3 whitespace-break-spaces break-all">
                    {JSON.stringify(values, null, 2)}
                  </pre>
                  <pre className="flex w-1/3 whitespace-break-spaces break-all max-h-72 overflow-auto">
                    {JSON.stringify(thisCameraConfigInStore, null, 2)}
                  </pre>
                </div>
              )}
              <HeaderView
                heading={t("heading_adas")}
                handleSave={() => {
                  navigationTypeRef.current = "none";
                  handleSubmit();
                }}
                handleSaveAndNext={() => {
                  navigationTypeRef.current = "next";
                  handleSubmit();
                }}
                handlePrevious={() => {
                  navigationTypeRef.current = "previous";
                  handleSubmit();
                }}
                handleLeftNavigation={handleLeftNavigation}
                handleRestoreToDefault={() => resetForm()}
                disabled={!isValid || isSubmitting ||
                  (!isSingleConfiguration && !isMassSingleConfiguration && cameraIds?.length < 1)
                }
                index={2}
              />
              <div className="bg-white grid grid-cols-12">
                <SubHeaderView
                  isSingleConfiguration={isSingleConfiguration}
                  isMassSingleConfiguration={isMassSingleConfiguration}
                  noOfCamera={cameraIds?.length}
                  cameraName={cameraName}
                />
                <form
                  className="col-span-12 flex Xgrid Xgrid-cols-12"
                  onSubmit={handleSubmit}
                >
                  <div className="w-80 Xcol-span-3 border-r mt-4">
                    <div className="font-semibold leading-none tracking-normal text-md mt-4 mx-4">
                      {t("subheading_alladas")}
                    </div>
                    <div className="w-full font-semibold text-sm leading-none tracking-normal py-4 border-b">
                      <div
                        key="adasEnable"
                        className={`w-full p-2 cursor-pointer hover:bg-accent-blue-paleO66`}
                        onClick={() => setSelectedEvent("adasEnable")}
                      >
                        <AdminFormFieldRadioCheckbox
                          label={
                            <span className="flex gap-2 me-2 my-1">
                              <span className="flex flex-col">
                                <span className="flex gap-2 text-sm text-accent-blue-dark">
                                  {t("adasEnable")}
                                  <img src={svgIcons.Info} className="w-5 h-5 shrink-0" />
                                </span>
                                <span className="leading-none text-xs font-normal text-gray-600">
                                  {t("adasEnableSubTitle")}
                                </span>
                              </span>
                            </span>
                          }
                          radioChecked={selectedEvent === "adasEnable"}
                          onRadioChange={() => setSelectedEvent("adasEnable")}
                          checked={allAdas}
                          onCheckboxChange={(e) => {
                            setAllAdas(prev => !prev);
                            ADAS_EVENTS.forEach((event) => {
                              setFieldValue(event, false);
                            });
                          }}
                          disabled={false}
                          isRadioButton={false}
                        />
                      </div>

                      <p className="px-4 font-normal text-xs leading-none text-gray-500">
                        {t("adas_description")}
                      </p>
                      <div className="font-semibold pt-4 leading-none tracking-normal text-base m-4">
                        {t("subheading_indadas")}
                      </div>
                      {ADAS_EVENTS?.map((adas_event: string, index) => {
                        return (
                          <div
                            key={index}
                            className={`w-full p-2 cursor-pointer hover:bg-accent-blue-paleO66`}
                            onClick={() => setSelectedEvent(adas_event)}
                          >
                            <AdminFormFieldRadioCheckbox
                              label={t(adas_event)}
                              radioChecked={selectedEvent === adas_event}
                              onRadioChange={() => setSelectedEvent(adas_event)}
                              checked={Boolean(values[adas_event as keyof typeof values])}
                              onCheckboxChange={() => {
                                setFieldValue(
                                  adas_event,
                                  !Boolean(values[adas_event as keyof typeof values])
                                );
                              }}
                              disabled={allAdas}
                              isRadioButton={true}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div
                    className={`flex-grow Xcol-span-9 flex flex-col Xgrid Xgrid-cols-6 ${Boolean(values[selectedEvent as keyof typeof values]) === false && !allAdas ? "pointer-events-none opacity-50" : ""}`}
                  >
                    <EventField
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      setFieldValue={setFieldValue}
                      handleBlur={handleBlur}
                      setFieldTouched={setFieldTouched}
                      selectedField={selectedEvent}
                      massConfigValues={massConfigValues}
                      setMassConfigValues={setMassConfigValues}
                    />
                  </div>
                </form>
              </div>
            </div>
          );
        }}
      </Formik>
    );
  }
  else {
    return (
      <Formik
        enableReinitialize={true}
        initialValues={memoizedInitialValues}
        validationSchema={aiEventDetectionFormValidationSchema}
        onSubmit={(values, { setSubmitting }) => {
          const navigationType = navigationTypeRef.current;
          const updatedValues: Record<string, any> = { ...values };
          const dmsMassConfig = allDms
            ? massConfigValues.dms ?? MASS_CONFIG_DEFAULTS
            : null;
          if (dmsMassConfig) {
            DMS_EVENT.forEach((event) => {
              const eventKey = event
                .replace(/^dms_/, "")
                .replace(/Enable$/, "");
              updatedValues[event] = true;

              updatedValues[`${eventKey}_sensitive`] =
                Number(dmsMassConfig.sensitive);

              updatedValues[`${eventKey}_videoBefore`] =
                Number(dmsMassConfig.videoBefore);

              updatedValues[`${eventKey}_videoAfter`] =
                Number(dmsMassConfig.videoAfter);

              updatedValues[`${eventKey}_eventDebounce`] =
                Number(dmsMassConfig.eventDebounce);

              updatedValues[`${eventKey}_media`] = dmsMassConfig.media;

              updatedValues[`${eventKey}_audioAlert`] = dmsMassConfig.audioAlert;
            });
          }
          if (allDms || DMS_EVENT.some((event) => Boolean(updatedValues[event]))) {
            updatedValues.dmsEnable = true;
          }
          const dmsPayload: AIEventConfiguration = buildEventPayload(
            updatedValues,
            DMS_EVENT
          );
          const payload: AIEventConfiguration = {
            dmsEnable: updatedValues.dmsEnable,
            ...dmsPayload,
          };

          const updateAllCameras = async () => {
            try {
              for (const id of cameraIds) {
                await updateConfigurationSingleApiDeviceTrigger({
                  cameraId: id,
                  aiEventDetection: payload,
                }).unwrap();
              }
              toast.success("All cameras updated successfully");
              if (navigationType === "next") {
                handleRightNavigation();
              } else if (navigationType === "previous") {
                handleLeftNavigation();
              }
            } catch (err: any) {
              console.error(err);
              toast.error("Failed to update one or more cameras");
            } finally {
              setSubmitting(false);
            }
          };
          if (isSingleConfiguration || isMassSingleConfiguration) {
            updateConfigurationSingleApiDeviceTrigger({
              cameraId: cameraId,
              aiEventDetection: payload,
            })
              .unwrap()
              .then((data) => {
                if (data.message && data.result) {
                  // update store with updated camera config
                  dispatch(
                    setSingleVMCameraCurrentConfigs({
                      cid: cameraId as string,
                      profile: {
                        ...thisCameraConfigInStore,
                        aiEventDetection: {
                          ...thisCameraConfigInStore.aiEventDetection,
                          ...payload,
                        },
                      },
                    })
                  );
                  toast.success("Updated Successfully");
                  if (navigationType === "next") {
                    handleRightNavigation();
                  } else if (navigationType === "previous") {
                    handleLeftNavigation();
                  }
                } else {
                  toast.error("Updation Failed");
                }
              })
              .catch((errors) => {
                console.error(errors);
                toast.error(errors?.join(""));
              })
              .finally(() => {
                setSubmitting(false);
              });
          } else {
            updateAllCameras();
          }
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          setFieldTouched,
          isSubmitting,
          dirty,
          isValid,
          resetForm,
        }) => (
          <div className="p-4 bg-gray-100 relative">
            <MassConfigSync group="dms" config={allDms ? massConfigValues.dms ?? MASS_CONFIG_DEFAULTS : null} />
            {isFetchingSingleCameraConfig && (
              <div className="absolute w-full h-full top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white/70 z-10 flex items-center justify-center">
                <img
                  src={svgIcons.LoadingAnimation}
                  alt=""
                  className="size-12"
                />
              </div>
            )}
            {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
              <div className="flex text-[10px]">
                <pre className="flex w-1/3 whitespace-break-spaces break-all">
                  {JSON.stringify(memoizedInitialValues, null, 2)}
                </pre>
                <pre className="flex w-1/3 whitespace-break-spaces break-all">
                  {JSON.stringify(values, null, 2)}
                </pre>
                <pre className="flex w-1/3 whitespace-break-spaces break-all max-h-72 overflow-auto">
                  {JSON.stringify(thisCameraConfigInStore, null, 2)}
                </pre>
              </div>
            )}
            <HeaderView
              heading={t("heading_dms")}
              handleSave={() => {
                navigationTypeRef.current = "none";
                handleSubmit();
              }}
              handleSaveAndNext={() => {
                navigationTypeRef.current = "next";
                handleSubmit();
              }}
              handlePrevious={() => {
                navigationTypeRef.current = "previous";
                handleSubmit();
              }}
              handleLeftNavigation={handleLeftNavigation}
              handleRestoreToDefault={() => resetForm()}
              disabled={
                !isValid ||
                isSubmitting ||
                (!isSingleConfiguration && !isMassSingleConfiguration && cameraIds?.length < 1)
              }
              index={3}
            />
            <div className="bg-white grid grid-cols-12">
              <SubHeaderView
                isSingleConfiguration={isSingleConfiguration}
                isMassSingleConfiguration={isMassSingleConfiguration}
                noOfCamera={cameraIds?.length}
                cameraName={cameraName}
              />
              <form
                className="col-span-12 flex Xgrid Xgrid-cols-12"
                onSubmit={handleSubmit}
              >

                <div className="w-80 Xcol-span-3 border-r mt-4">
                  <div className="font-semibold leading-none tracking-normal text-md mt-4 mx-4">
                    {t("subheading_alldms")}
                  </div>
                  <div className="w-full font-semibold text-sm leading-none tracking-normal py-4 border-b">
                    <div
                      key="dmsEnable"
                      className={`w-full p-2 cursor-pointer hover:bg-accent-blue-paleO66`}
                      onClick={() => setSelectedEvent("dmsEnable")}
                    >
                      <AdminFormFieldRadioCheckbox
                        label={
                          <span className="flex gap-2 me-2 my-1">
                            <span className="flex flex-col">
                              <span className="flex gap-2 text-sm text-accent-blue-dark">
                                {t("dmsEnable")}
                                <img src={svgIcons.Info} className="w-5 h-5 shrink-0" />
                              </span>
                              <span className="leading-none text-xs font-normal text-gray-600">
                                {t("dmsEnableSubTitle")}
                              </span>
                            </span>
                          </span>
                        }
                        radioChecked={selectedEvent === "dmsEnable"}
                        onRadioChange={() => setSelectedEvent("dmsEnable")}
                        checked={allDms}
                        onCheckboxChange={() => {
                          setAllDms(prev => !prev);
                          DMS_EVENT.forEach((event) => {
                            setFieldValue(event, false);
                          });
                        }}
                        disabled={false}
                        isRadioButton={false}
                      />
                    </div>

                    <p className="px-4 font-normal text-xs leading-none text-gray-500">
                      {t("dms_description")}
                    </p>
                    <div className="font-semibold pt-4 leading-none tracking-normal text-base m-4">
                      {t("subheading_inddms")}
                    </div>
                    {DMS_EVENT?.map((dms_event: string, index) => {
                      return (
                        <div
                          key={index}
                          className={`w-full p-2 cursor-pointer hover:bg-accent-blue-paleO66`}
                          onClick={() => setSelectedEvent(dms_event)}
                        >
                          <AdminFormFieldRadioCheckbox
                            label={t(dms_event)}
                            radioChecked={selectedEvent === dms_event}
                            onRadioChange={() => setSelectedEvent(dms_event)}
                            checked={Boolean(values[dms_event as keyof typeof values])}
                            onCheckboxChange={() => {
                              setFieldValue(
                                dms_event,
                                !Boolean(values[dms_event as keyof typeof values])
                              );
                            }}
                            disabled={allDms}
                            isRadioButton={true}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div
                  className={`flex-grow Xcol-span-9 flex flex-col Xgrid Xgrid-cols-6 ${Boolean(values[selectedEvent as keyof typeof values]) === false && !allDms ? "pointer-events-none opacity-50" : ""}`}
                >
                  <EventField
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                    handleBlur={handleBlur}
                    setFieldTouched={setFieldTouched}
                    selectedField={selectedEvent}
                    massConfigValues={massConfigValues}
                    setMassConfigValues={setMassConfigValues}
                  />
                </div>
              </form>
            </div>
          </div>
        )}
      </Formik>
    );
  }

};

export default ScreenAIEventDetction;
